import { FC, useState, useEffect, useLayoutEffect } from "react";
import { Field, FieldProps, Form, Formik } from "formik";
import * as Yup from "yup";
import { useNavigate, useOutletContext } from "react-router-dom";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { Input } from "@capacit-com/lara-law-shared-ui";
import { Label } from "@capacit-com/lara-law-shared-ui";
import { Textarea } from "@capacit-com/lara-law-shared-ui";
import { Checkbox } from "@capacit-com/lara-law-shared-ui";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@capacit-com/lara-law-shared-ui";
import useWorkspace from "@/hooks/useWorkspace";
import AppRoutes from "@/constants/routes/AppRoutes";
import WorkspaceUsers from "../WorkspaceUsers";
import { WorkspaceRole } from "@/models/workspace/Workspace";
import { getUserRoleLabel } from "../../utils/workspaceRoleDisplay";
import DeleteDialog from "@/components/dialogs/delete-dialog";
import { Spinner } from "@capacit-com/lara-law-shared-ui";
import {
  showErrorToast,
  showSuccessToast,
} from "@/components/custom-toasts/toasts";
import { logger } from "@/lib/logger";
import useTenant from "@/hooks/useTenant";
import Tenant from "@/models/tenant/tenant";
import { Building2 } from "lucide-react";
import { useWorkspaceContext } from "@/pages/workspace/providers/WorkspaceContext";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@capacit-com/lara-law-shared-ui";

type OutletContext = {
  isDeleteLoading: boolean;
  setIsDeleteLoading: (isDeleteLoading: boolean) => void;
};

type FormValues = {
  name: string;
  description: string;
  sharedWithWholeTenant: boolean;
  sharedWorkspaceRole?: WorkspaceRole;
  sharedWithSubTenants?: string[];
};

const SettingsTab: FC = () => {
  const {
    workspace,
    isUserAdmin,
    isParentWorkspace,
    isOrphanedInspection,
    setLoading,
    canChangeOrgSharing,
    canDeleteWorkspace,
  } = useWorkspaceContext();

  const { isDeleteLoading, setIsDeleteLoading } =
    useOutletContext<OutletContext>();
  const { update, remove } = useWorkspace();
  const navigate = useNavigate();
  const { useQueryGetAvailableSubtenants } = useTenant();
  const [availableSubtenants, setAvailableSubtenants] = useState<Tenant[]>([]);
  const fetchedAvailableSubtenants = useQueryGetAvailableSubtenants();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useLayoutEffect(() => {
    if (!workspace) return;

    setLoading(true);
    const forbidden =
      isParentWorkspace || isOrphanedInspection || !isUserAdmin();

    if (forbidden) {
      if (isOrphanedInspection) {
        navigate(AppRoutes.workspaceFiles(workspace.id), { replace: true });
      } else if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate(AppRoutes.workspaces, { replace: true });
      }
    }

    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace, isParentWorkspace, isOrphanedInspection]);

  useEffect(() => {
    if (
      fetchedAvailableSubtenants.isFetched &&
      fetchedAvailableSubtenants.data
    ) {
      setAvailableSubtenants(fetchedAvailableSubtenants.data);
    }
  }, [fetchedAvailableSubtenants.isFetched, fetchedAvailableSubtenants.data]);

  const handleDeleteConfirmation = async () => {
    try {
      if (workspace?.id) {
        setIsDeleteLoading(true);

        await remove.mutateAsync(workspace.id);

        showSuccessToast("Workspace slettet");

        navigate(AppRoutes.workspaces);
        setDeleteDialogOpen(false);
        setIsDeleteLoading(false);
      }
    } catch (error) {
      logger.error("Failed to delete workspace", {
        operation: "deleteWorkspace",
        error,
        workspaceId: workspace?.id,
      });
      showErrorToast("Der skete en fejl. Prøv igen senere.");
      setDeleteDialogOpen(false);
      setIsDeleteLoading(false);
    }
  };

  const initialValues: FormValues = {
    name: workspace?.name || "",
    description: workspace?.description || "",
    sharedWithWholeTenant: workspace?.sharedWithWholeTenant || false,
    sharedWorkspaceRole: workspace?.sharedWorkspaceRole || undefined,
    sharedWithSubTenants: workspace?.sharedWithSubTenants || [],
  };

  // Helper function to check if current values differ from initial values
  const hasActualChanges = (currentValues: FormValues): boolean => {
    return (
      currentValues.name !== initialValues.name ||
      currentValues.description !== initialValues.description ||
      currentValues.sharedWithWholeTenant !==
        initialValues.sharedWithWholeTenant ||
      currentValues.sharedWorkspaceRole !== initialValues.sharedWorkspaceRole ||
      JSON.stringify(currentValues.sharedWithSubTenants || []) !==
        JSON.stringify(initialValues.sharedWithSubTenants || [])
    );
  };

  const validationSchema = Yup.object({
    name: Yup.string().required("Navn er påkrævet"),
    sharedWithWholeTenant: Yup.boolean(),
    sharedWorkspaceRole: Yup.string().when("sharedWithWholeTenant", {
      is: true,
      then: (schema) =>
        schema.required(
          "Vælg venligst et adgangsniveau når workspacet deles med organisationen"
        ),
      otherwise: (schema) => schema.nullable(),
    }),
    sharedWithSubTenants: Yup.array().when("sharedWithWholeTenant", {
      is: false,
      then: (schema) =>
        schema.test(
          "subtenants-require-org-sharing",
          "Du skal aktivere organisationsdeling for at dele med underorganisationer",
          function (value) {
            if (value && value.length > 0) {
              return this.parent.sharedWithWholeTenant === true;
            }
            return true;
          }
        ),
      otherwise: (schema) => schema.nullable(),
    }),
  });

  const handleSubmit = async (values: FormValues) => {
    try {
      const submitValues = {
        id: workspace?.id,
        name: values.name,
        description: values.description,
        sharedWithWholeTenant: values.sharedWithWholeTenant,
        sharedWorkspaceRole: values.sharedWithWholeTenant
          ? values.sharedWorkspaceRole
          : undefined,
        sharedWithSubTenants: values.sharedWithSubTenants || [],
      };

      await update.mutateAsync(submitValues);

      showSuccessToast("Workspace opdateret");
    } catch (error) {
      logger.error("Failed to update workspace settings", {
        operation: "updateWorkspaceSettings",
        error,
        workspaceId: workspace?.id,
      });

      showErrorToast("Der skete en fejl. Prøv igen senere.");
    }
  };

  const forbidden =
    !!workspace &&
    (isParentWorkspace || isOrphanedInspection || !isUserAdmin());

  if (!workspace) return null;
  if (forbidden) return null;

  return (
    <div className="relative h-full">
      {/* Delete Overlay */}
      {isDeleteLoading && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-background/50 backdrop-blur-[1.5px] rounded-sm">
          <div className="flex items-center gap-2">
            <Spinner />
            <p className="text-muted-foreground">Sletter...</p>
          </div>
        </div>
      )}

      <div
        className={`flex flex-col gap-10 pb-5 ${
          isDeleteLoading ? "pointer-events-none" : ""
        }`}
      >
        {/* General Section */}
        <section className="space-y-6">
          <h2 className="text-2xl font-semibold">Generelt</h2>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ handleSubmit, values }) => (
              <Form onSubmit={handleSubmit}>
                <div className="flex flex-col w-full gap-6">
                  <Field name="name">
                    {({ field, meta }: FieldProps) => (
                      <div className="space-y-2">
                        <Label htmlFor="name">Navn</Label>
                        <Input
                          id="name"
                          type="text"
                          {...field}
                          data-testid="name"
                        />
                        {meta.touched && meta.error && (
                          <p className="text-sm text-destructive">{meta.error}</p>
                        )}
                      </div>
                    )}
                  </Field>

                  <Field name="description">
                    {({ field, meta }: FieldProps) => (
                      <div className="space-y-2">
                        <Label htmlFor="description">Beskrivelse</Label>
                        <Textarea
                          id="description"
                          {...field}
                          data-testid="description"
                        />
                        {meta.touched && meta.error && (
                          <p className="text-sm text-destructive">{meta.error}</p>
                        )}
                      </div>
                    )}
                  </Field>

                  <Field name="sharedWithWholeTenant">
                    {({ field, form, meta }: FieldProps) => (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Label className="flex items-center gap-1.5">
                            Offentlig for hele organisationen
                          </Label>
                          <div className="flex items-center space-x-2">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Checkbox
                                  disabled={!canChangeOrgSharing()}
                                  checked={field.value}
                                  onCheckedChange={(checked) => {
                                    form.setFieldValue(
                                      "sharedWithWholeTenant",
                                      checked
                                    );
                                    if (!checked) {
                                      form.setFieldValue(
                                        "sharedWorkspaceRole",
                                        undefined
                                      );
                                    } else {
                                      form.setFieldValue(
                                        "sharedWorkspaceRole",
                                        WorkspaceRole.READ
                                      );
                                    }
                                  }}
                                />
                              </TooltipTrigger>
                              {!canChangeOrgSharing() && (
                                <TooltipContent>
                                  <p>
                                    Du har ikke rettigheder til at ændre deling
                                    af workspacet med hele organisationen.
                                  </p>
                                </TooltipContent>
                              )}
                            </Tooltip>
                          </div>
                        </div>
                        {meta.touched && meta.error && (
                          <p className="text-sm text-destructive">{meta.error}</p>
                        )}
                      </div>
                    )}
                  </Field>

                  <Field name="sharedWorkspaceRole">
                    {({ field, form, meta }: FieldProps) => (
                      <div
                        className={
                          form.values.sharedWithWholeTenant
                            ? "space-y-2"
                            : "hidden"
                        }
                      >
                        {form.values.sharedWithWholeTenant && (
                          <>
                            <Label className="flex items-center gap-1.5">
                              Vælg adgangsniveau for organisationen
                            </Label>
                            <Select
                              disabled={!canChangeOrgSharing()}
                              value={field.value || ""}
                              onValueChange={(value) => {
                                form.setFieldValue(
                                  "sharedWorkspaceRole",
                                  value
                                );
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Vælg adgangsniveau" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={WorkspaceRole.READ}>
                                  {getUserRoleLabel(WorkspaceRole.READ)}
                                </SelectItem>
                                <SelectItem value={WorkspaceRole.WRITE}>
                                  {getUserRoleLabel(WorkspaceRole.WRITE)}
                                </SelectItem>
                                <SelectItem value={WorkspaceRole.ADMIN}>
                                  {getUserRoleLabel(WorkspaceRole.ADMIN)}
                                </SelectItem>
                              </SelectContent>
                            </Select>
                            {meta.touched && meta.error && (
                              <p className="text-sm text-destructive">
                                {meta.error}
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </Field>

                  <Field name="sharedWithSubTenants">
                    {({ field, form, meta }: FieldProps) => {
                      if (
                        availableSubtenants.length === 0 ||
                        availableSubtenants[0].id ===
                          "00000000-0000-0000-0000-000000000000"
                      )
                        return <></>;

                      const hasSelectedSubtenants =
                        field.value && field.value.length > 0;
                      const isOrgSharingEnabled =
                        form.values.sharedWithWholeTenant;
                      const showWarning =
                        hasSelectedSubtenants && !isOrgSharingEnabled;

                      return (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label className="flex items-center gap-1.5">
                              <Building2 className="h-3.5 w-3.5 text-primary" />
                              Del med underorganisationer
                            </Label>
                            <div className="flex items-center space-x-2">
                              <label
                                htmlFor="select-all-subtenants"
                                className="text-sm font-medium leading-none cursor-pointer"
                              >
                                Vælg alle
                              </label>
                              <Checkbox
                                id="select-all-subtenants"
                                checked={
                                  field.value?.length ===
                                    availableSubtenants.length &&
                                  availableSubtenants.length > 0
                                }
                                className="hover:cursor-pointer"
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    form.setFieldValue(
                                      "sharedWithSubTenants",
                                      availableSubtenants.map(
                                        (subtenant) => subtenant.id
                                      )
                                    );
                                  } else {
                                    form.setFieldValue(
                                      "sharedWithSubTenants",
                                      []
                                    );
                                  }
                                }}
                              />
                            </div>
                          </div>

                          {showWarning && (
                            <div className="text-xs text-warning bg-warning/10 p-2 rounded-md border border-warning/30">
                              <strong>Bemærk:</strong> For at dele med
                              underorganisationer skal du først aktivere
                              "Offentlig for hele organisationen" ovenfor.
                            </div>
                          )}
                          <div className="border rounded-md p-3 max-h-48 overflow-y-auto">
                            <div className="space-y-2 grid grid-cols-2 gap-2">
                              {availableSubtenants.map((subtenant) => (
                                <div
                                  key={subtenant.id}
                                  className="flex items-center space-x-2 mb-0"
                                >
                                  <Checkbox
                                    id={`subtenant-${subtenant.id}`}
                                    checked={
                                      field.value?.includes(subtenant.id) ||
                                      false
                                    }
                                    onCheckedChange={(checked) => {
                                      const currentValues = field.value || [];
                                      if (checked) {
                                        form.setFieldValue(
                                          "sharedWithSubTenants",
                                          [...currentValues, subtenant.id]
                                        );
                                      } else {
                                        form.setFieldValue(
                                          "sharedWithSubTenants",
                                          currentValues.filter(
                                            (id: string) => id !== subtenant.id
                                          )
                                        );
                                      }
                                    }}
                                  />
                                  <label
                                    htmlFor={`subtenant-${subtenant.id}`}
                                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                                  >
                                    {subtenant.name}
                                  </label>
                                </div>
                              ))}
                            </div>
                          </div>
                          <p className="text-xs">
                            Obs. Underorganisationer får kun læse rettigheder
                          </p>
                          {meta.touched && meta.error && (
                            <p className="text-sm text-destructive">{meta.error}</p>
                          )}
                        </div>
                      );
                    }}
                  </Field>

                  <div>
                    <Button
                      type="submit"
                      disabled={update.isPending || !hasActualChanges(values)}
                    >
                      {update.isPending ? (
                        <>
                          <Spinner className="text-primary-foreground size-6" />{" "}
                          Gemmer...
                        </>
                      ) : (
                        "Gem ændringer"
                      )}
                    </Button>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </section>

        {/* Users Section */}
        {workspace && (
          <section className="space-y-6">
            <h2 className="text-2xl font-semibold">Brugere</h2>
            <WorkspaceUsers />
          </section>
        )}

        {/* Danger Zone Section */}
        <section className="space-y-6">
          <h2 className="text-2xl font-semibold text-destructive">
            Danger Zone
          </h2>

          <DeleteDialog
            isOpen={deleteDialogOpen}
            setIsOpen={setDeleteDialogOpen}
            isDeleteLoading={isDeleteLoading}
            onDelete={handleDeleteConfirmation}
            entityName={workspace?.name || ""}
            isDisabled={!canDeleteWorkspace()}
          />
        </section>
      </div>
    </div>
  );
};

export default SettingsTab;
